import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, useLocation } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { AlertProvider } from './contexts/alert-context.tsx';
import { TeamProvider } from './contexts/team-context.tsx';

import reportWebVitals from './reportWebVitals';
import Homepage from './pages/homepage';
import LoginRedirect from './pages/login-redirect/index.tsx';
import Dashboard from './pages/dashboard';
import Team from './pages/team/index.tsx';
import LogoutRedirect from './pages/logout-redirect/index.tsx';
import UserProfile from './pages/profile/index.tsx';
import AuthGuard from './guards/auth.guard.tsx';
import Logout from './pages/dashboard/logout/index.jsx';
import AcceptInvite from './pages/accept-invite/index.tsx';
import TeamLayout from './pages/team-layout.tsx/index.tsx';
import StaffLayout from './pages/staff-layout.tsx/index.tsx';
import NotFound from './pages/404/index.tsx';

import './index.css';
import StixifyReportAdd from './pages/stixify/reports/add-new-profile.tsx';
import Reports from './pages/stixify/reports/index.tsx';
import ReportExtractionPage from './pages/stixify/reports/extraction.tsx';
import Jobs from './pages/stixify/jobs/index.tsx';
import JobDetailsComponent from './pages/stixify/jobs/job-details.tsx';
import ObservableSearchPage from './pages/stixify/search/extraction-search.tsx';
import ExtractionReportsPage from './pages/stixify/search/extraction-reports.tsx';
import StixifyProfiles from './pages/stixify/profile/index.tsx';
import TeamStixifyProfileAdd from './pages/stixify/profile/add-new-profile.tsx';
import TeamDossiers from './pages/stixify/dossier/index.tsx';
import AddDossierPage from './pages/stixify/dossier/add-new.tsx';
import DossierDetail from './pages/stixify/dossier/details.tsx';
import EditDossierPage from './pages/stixify/dossier/edit-dossier.tsx';
import TeamStixifyProfileDetail from './pages/stixify/profile/profile_detail.tsx';
import StixifyProfileEdit from './pages/stixify/profile/edit-profile.tsx';
import Layout from './pages/layout.tsx/index.tsx';
import IntegrationsPage from "./pages/integrations/index.tsx";

const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN;
const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID;

const router = createBrowserRouter([
  {
    path: '/',
    Component: Layout,
    children: [
      {
        path: '/',
        Component: Homepage,
      },
      {
        path: '/login-redirect',
        Component: LoginRedirect,
      },
      {
        path: '/logout',
        Component: Logout,
      },
      {
        path: '/logout-redirect',
        Component: LogoutRedirect,
      },
      {
        path: '/login',
        Component: LogoutRedirect,
      },
      {
        path: '/home',
        Component: Dashboard,
      },
      {
        path: '',
        element: <AuthGuard>
          <Dashboard />
        </AuthGuard>,
        children: [
          {
            path: "/integrations",
            Component: IntegrationsPage,
          },
          {
            path: 'user/account',
            Component: UserProfile,
          },
          {
            path: '',
            Component: TeamLayout,
            children: [
              {
                path: 'teams/:teamId',
                Component: Team,
              },
              {
                path: ':teamId/jobs',
                Component: Jobs,
              },
              {
                path: ':teamId/:reportId/jobs/:id',
                Component: JobDetailsComponent,
              },
              {
                path: 'reports',
                Component: Reports,
              },
              {
                path: 'reports/add',
                Component: StixifyReportAdd,
              },
              {
                path: 'intelligence',
                Component: ObservableSearchPage,
              },
              {
                path: 'intelligence/:objectId',
                Component: ExtractionReportsPage,
              },
              {
                path: 'reports/:id',
                Component: ReportExtractionPage,
              },
              {
                path: 'teams/:teamId/profiles/:id',
                Component: TeamStixifyProfileDetail,
              },
              {
                path: 'teams/:teamId/profiles/add',
                Component: TeamStixifyProfileAdd,
              },
              {
                path: 'dossiers',
                Component: TeamDossiers,
              },
              {
                path: 'dossiers/add',
                Component: AddDossierPage,
              },
              {
                path: 'dossiers/:id/edit',
                Component: EditDossierPage,
              },
              {
                path: 'dossiers/:id',
                Component: DossierDetail,
              },
            ],
          },
          {
            path: 'staff',
            component: StaffLayout,
            children: [
              {
                path: 'dossiers',
                Component: TeamDossiers,
              },
              {
                path: 'dossiers/:id',
                Component: DossierDetail,
              },
              {
                path: 'reports',
                Component: Reports,
              },
              {
                path: 'reports/:id',
                Component: ReportExtractionPage,
              },
              {
                path: 'jobs',
                Component: Jobs,
              },
              {
                path: 'profiles',
                Component: StixifyProfiles,
              },
              {
                path: 'profiles/:id',
                Component: TeamStixifyProfileDetail,
              },
              {
                path: 'profiles/:id/edit',
                Component: StixifyProfileEdit,
              },
              {
                path: 'reports/:reportId/jobs/:id',
                Component: JobDetailsComponent,
              },
            ],
          },
          {
            path: '*',
            Component: NotFound,
          },
        ],
      },
      {
        path: 'teams/invitation/:invite_id',
        Component: AcceptInvite,
      },
    ]
  },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  // <React.StrictMode>
  <Auth0Provider
    domain={AUTH0_DOMAIN}
    clientId={AUTH0_CLIENT_ID}
    authorizationParams={{
      redirect_uri: `${window.location.origin}/login-redirect`,
    }}
  >
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <AlertProvider>
        <TeamProvider>
          <RouterProvider router={router} >
          </RouterProvider>
        </TeamProvider>
      </AlertProvider>
    </LocalizationProvider>
  </Auth0Provider>,
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
